<script>
  import SettingsLayout from '../../components/SettingsLayout.svelte';
  import { settingsStore } from '../../stores/settingsStore.js';

  const {
    formData,
    jobtreadConnectionStatus,
    jobtreadOrgName,
    jobtreadConnected,
    availableOrganizations,
    fetchingOrganizations,
    showManualOrgId,
    jobtreadManagedByOwner,
    fetchOrganizations,
    checkJobTreadConnection,
    clearJobtreadOrgOnKeyChange
  } = settingsStore;
</script>

<SettingsLayout activeTab="jobtread" tabTitle="JobTread Connection">
  <div class="space-y-4">
    {#if $jobtreadManagedByOwner}
      <div class="p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p class="text-sm text-blue-800">
          Your JobTread connection is managed by your organisation owner and is shown read-only.
        </p>
      </div>
    {/if}
    <div class="flex items-center gap-3 mb-2">
      {#if $jobtreadConnected}
        <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300">
          <span class="w-1.5 h-1.5 mr-1.5 rounded-full bg-green-500"></span>
          Connected
        </span>
      {:else}
        <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-300">
          <span class="w-1.5 h-1.5 mr-1.5 rounded-full bg-red-500"></span>
          Not Connected
        </span>
      {/if}
    </div>

    <div class="p-3 bg-blue-50 border border-blue-200 rounded-md">
      <p class="text-sm text-blue-800 mb-2">
        📖 <strong>Setup your JobTread Connection:</strong>
      </p>
      <ol class="text-sm text-blue-800 list-decimal list-inside space-y-1 mb-3">
        <li><strong>Step 1:</strong> Go to <a href="https://app.jobtread.com/settings/integrations/api/grants" target="_blank" class="text-blue-600 underline hover:text-blue-800">API Grants</a></li>
        <li><strong>Step 2:</strong> Click "Add Grant" and name it <strong>Buildzilla</strong></li>
        <li><strong>Step 3:</strong> Copy the grant key and paste it below</li>
        <li><strong>Step 4:</strong> Click "Fetch Organizations" to automatically load your organizations</li>
      </ol>
      <img src="/images/apigrants.jpg" alt="JobTread API Grant Example" class="w-full max-w-md mx-auto rounded border border-blue-300 shadow-sm" />
    </div>

    <div>
      <label for="jobtreadApiKey" class="block text-sm font-medium text-gray-700 mb-2">
        Grant Key *
      </label>
      <input
        type="text"
        id="jobtreadApiKey"
        bind:value={$formData.jobtreadApiKey}
        on:input={clearJobtreadOrgOnKeyChange}
        readonly={$jobtreadManagedByOwner}
        class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 placeholder-red-500 read-only:bg-gray-100 read-only:cursor-not-allowed"
        placeholder="Enter Grant Key"
      />
      <button
        type="button"
        on:click={fetchOrganizations}
        disabled={$fetchingOrganizations || !$formData.jobtreadApiKey.trim() || $jobtreadManagedByOwner}
        class="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        {#if $fetchingOrganizations}
          <span class="flex items-center">
            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            Fetching...
          </span>
        {:else}
          Fetch Organizations
        {/if}
      </button>
    </div>

    <div>
      <label class="block text-sm font-medium text-gray-700 mb-2">
        Organisation ID *
      </label>
      {#if $availableOrganizations.length > 1}
        <select
          bind:value={$formData.jobtreadOrgId}
          class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          required
        >
          <option value="">-- Select an Organization --</option>
          {#each $availableOrganizations as org}
            <option value={org.id}>{org.name}</option>
          {/each}
        </select>
      {:else if $showManualOrgId || ($formData.jobtreadOrgId && $availableOrganizations.length === 0)}
        <input
          type="text"
          id="jobtreadOrgId"
          bind:value={$formData.jobtreadOrgId}
          class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="Enter Organisation ID"
        />
      {:else}
        <p class="text-sm text-gray-500 italic">
          Click "Fetch Organizations" above, or
          <button type="button" on:click={() => showManualOrgId.set(true)} class="text-blue-600 hover:text-blue-800 underline bg-transparent border-0 p-0">enter Organisation ID manually</button>.
        </p>
      {/if}
    </div>

    {#if $formData.jobtreadOrgId && $availableOrganizations.length === 1}
      <div class="p-3 bg-green-50 border border-green-200 rounded-md">
        <p class="text-sm text-green-700">
          Organization: <strong>{$jobtreadOrgName}</strong>
        </p>
      </div>
    {/if}

    <div>
      <button
        type="button"
        on:click={checkJobTreadConnection}
        disabled={$jobtreadConnectionStatus === 'checking' || !$formData.jobtreadApiKey.trim() || !$formData.jobtreadOrgId.trim() || $jobtreadManagedByOwner}
        class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {#if $jobtreadConnectionStatus === 'checking'}
          <span class="flex items-center">
            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            Checking Connection...
          </span>
        {:else}
          Check JobTread Connection
        {/if}
      </button>

      {#if $jobtreadConnectionStatus === 'success'}
        <div class="mt-2 p-3 bg-green-50 border border-green-200 rounded-md">
          <p class="text-sm text-green-700 font-medium">
            ✅ Connected to: {$jobtreadOrgName}
          </p>
          <p class="text-xs text-green-600 mt-1">
            Settings saved automatically
          </p>
        </div>
      {:else if $jobtreadConnectionStatus === 'error'}
        <div class="mt-2 text-sm text-red-600">
          ❌ Connection failed
        </div>
      {/if}
    </div>
  </div>
</SettingsLayout>
